<template lang="pug">
//- ESTRUCTURA DEL COMPONENTE:
//- .Ui2Boton (contenedor)
//-   ├─ button (transparente, cubre todo el área, recibe los clicks)
//-   ├─ .elBorde
//-   └─ .contenidoBoton (elementos visibles)
//-       ├─ .zonaIcono.preIcono (slot)
//-       ├─ .contenido (slot default)
//-       └─ .zonaIcono.postIcono (slot)
//-
//- IMPORTANTE PARA TESTING:
//- - El <button> está ADYACENTE a .contenidoBoton, NO dentro
//- - Para hacer click: buscar `.Ui2Boton button`
//- - El <button> es transparente y cubre todo el contenido visible
//-
.Ui2Boton(
  :class="[clasesBoton, { cargando, preIcono: slots.preIcono, postIcono: slots.postIcono, simetrico, oscuro: !unref(TemaOscuroAPI.claro), intocable: !!intocable }]"
  :style="estilosInlineBoton"
  :disabled="disabled || undefined"
  )
  button(
    v-bind="subsetAttrs"
    :type="type"
    :aria-label="(textoAccesible ?? (typeof $slots.default === 'string' ? $slots.default : 'Boton')).toString()"
    @click.prevent="emitirClickConCalma"
    @mousedown="emitirSiCorresponde"
    @touchstart.passive="detectorToque"
    :disabled="disabled || undefined") 

  .elBorde
  .contenidoBoton.ffrn.aic.gap05em(:class="{ alCentro }")
    .zonaIcono.preIcono.fa00.aic(v-if="cargando || slots.preIcono || (slots.postIcono)")
      div(v-if="cargando")
        UiLoader

      div.fz12rem(v-else-if="slots.preIcono")
        slot(name="preIcono")

    .contenido(v-if="slots.default")
      slot(name="default")

    .zonaIcono.postIcono.fa00.minw1(v-if="(simetrico && slots.preIcono) || slots.postIcono || cargando")
      div.fz12rem(v-if="slots.postIcono")
        slot(name="postIcono")
      div(v-else-if="cargando")
        .w1.h1
</template>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    cargando?: boolean
    textoAccesible?: string
    alCentro?: boolean
    simetrico?: boolean
    intocable?: boolean
    disabled?: boolean
    type?: 'button' | 'submit'
    emitirAlPresionar?: boolean
  }>(),
  {
    cargando: false,
    alCentro: false,
    simetrico: false,
    intocable: false,
    disabled: undefined,
    type: 'button',
    emitirAlPresionar: false,
  }
)

const clasesBoton = computed(() => {
  const attrs = useAttrs()
  return attrs.class
})
const estilosInlineBoton = computed(() => {
  const attrs = useAttrs()
  return attrs.style as any
})

const emit = defineEmits(['click'])

defineOptions({
  inheritAttrs: false
})

defineComponent({ name: 'Ui2Boton' })

const slots = useSlots()

const { cargando, alCentro, simetrico, disabled } = toRefs(props)

const subsetAttrs = computed(() => {
  const attrs = useAttrs()
  return lodash.omit(attrs, ['class', 'style']);
})

function emitirClick(event: MouseEvent) {
  if (unref(props.intocable)) {
    consolo.log('[boton]', 'emitirClick detenido por intocable')
    return
  }
  if (unref(props.cargando)) {
    consolo.log('[boton]', 'emitirClick detenido por cargando')
    event.preventDefault()
    return
  }
  consolo.log('[boton]', 'emitirClick')
  emit('click', event)
}

const emitirClickConCalma = lodash.throttle(emitirClick, 300, { leading: true, trailing: false })

function emitirSiCorresponde(event: MouseEvent) {
  if (!unref(props.emitirAlPresionar))
    return
  event.preventDefault()
  emitirClickConCalma(event)
}
</script>

<style lang="sass">
@use 'sass:color'
@use "sass:math"
@use '@base/sass/comun'

.Ui2Boton
  text-align: center
  position: relative
  z-index: 1
  cursor: pointer
  line-height: 1.4
  user-select: none

  outline: 0
  border: 0 solid transparent
  button
    position: absolute
    z-index: 5
    left: 0
    right: 0
    top: 0
    bottom: 0
    border-radius: inherit
    border-color: inherit
    opacity: 0
    cursor: inherit


  &::before
    z-index: 0
  &::after
    z-index: 1
  &::before,
  &::after
    content: ''
    +comun.maximizar
    border-radius: inherit
    transition: inherit

  &.preIcono:not(.postIcono):not(.anchoComun):not(.simetrico)
    .postIcono
      display: none
  &.postIcono:not(.preIcono):not(.anchoComun):not(.simetrico)
    .preIcono
      display: none
  .contenidoBoton
    position: relative
    z-index: 2
    height: 100%
    width: 100%
    &.alCentro
      justify-content: center
      .contenido
        flex: auto 0 1
        display: flex
        align-items: center
        line-height: 1.2
    .contenido
      flex: auto 1 1
      max-width: 100%


  .zonaIcono
    min-width: 1em
    min-height: 1em
    line-height: 0
  &.vertical
    .contenidoBoton
      flex-direction: column
      justify-content: center
      align-items: center

    .zonaIcono
      &.preIcono
        margin-bottom: 0.2em
        margin-right: 0
      &.postIcono
        margin-top: .5em
        margin-left: 0

  &:not(.sinPadding)
    $min: #{math.div(14,17)}rem
    $lado: #{math.div(20,17)}rem
    padding: $min $lado
    &.circular,
    &.cuadrado
      padding: $min
      $min: #{math.div(7,17)}rem
      $lado: #{math.div(12,17)}rem
      width: calc($lado * 3)
      height: calc($lado * 3)
    &.chico
      $min: #{math.div(7,17)}rem
      $lado: #{math.div(14,17)}rem
      padding: $min $lado
      &.circular,
      &.cuadrado
        padding: $min
    &.mini
      $min: #{math.div(4,17)}rem
      $lado: #{math.div(10,17)}rem
      padding: $min $lado
      &.circular,
      &.cuadrado
        padding: $min
    &.vertical
      padding-top: 1em
      padding-bottom: 1em

  // * Radio

  &:not(.radioS):not(.radioM):not(.radio):not(.radioL):not(.sinRadio):not(.cuadrado)
    border-radius: .75rem
    &.rectangular
      border-radius: .4rem
    &.redondeado,
    &.circular,
    &.chico,
    &.mini
      border-radius: 10em

  // * Altura y ancho mínimos

  // &:not(.sinMinimos)
  // 	// min-height: #{math.div(50,17)}rem
  // 	// min-width: #{math.div(50,17)}rem
  // 	&.chico
  // 		min-height: #{math.div(34,17)}rem
  // 		min-width: #{math.div(34,17)}rem
  // 	&.mini
  // 		min-height: #{math.div(28,17)}rem
  // 		min-width: #{math.div(28,17)}rem

  // Font sizes
  &:not(.sinFz)
    +comun.textoDelBody
    &.chico,
    &.mini
      +comun.subencabezado
      &.circular
        line-height: 1.2


  &.cuadrado,
  &.circular
    .contenidoBoton
      width: 100%
    .contenido
      display: none
    .postIcono
      display: none
  &.circular
    line-height: 1.2
    .contenidoBoton
      justify-content: center
      align-items: center
    .zonaIcono
      min-width: 0

      &.preIcono
        margin-right: 0
      &.postIcono
        margin-left: 0

  &.cuadrado
    border-radius: comun.$radioS
    .contenidoBoton
      justify-content: center
      align-items: center
    .zonaIcono
      min-width: 0
      &.preIcono
        margin-right: 0
      &.postIcono
        margin-left: 0

@keyframes moverFondo 
  0% 
    background-position: 0% 0
  100% 
    background-position: 500% 0


// Colores
.Ui2Boton
  color: var(--botonColor)
  &::before
    background-color: inherit
    background-color: var(--botonFondo)
  &::after
    content: ''
    z-index: 4

  &.body
    &Color
      color: var(--bodyColor)
    &Fondo::before
      background-color: var(--bodyFondo)
  &.blanco
    &Color
      color: white
    &Fondo::before
      background-color: white
  &.negro
    &Color
      color: black
    &Fondo::before
      background-color: black
  &.primario
    &Color
      color: var(--primarioColor)
    &Fondo::before
      background-color: var(--primarioFondo)
  &.link
    &Color
      color: var(--linkColor)
    &Fondo::before
      background-color: var(--linkFondo)
  &.activo
    &Color
      color: var(--activoColor)
    &Fondo::before
      background-color: var(--activoFondo)
  &.exito
    &Color
      color: var(--exitoColor)
    &Fondo::before
      background-color: var(--exitoFondo)
  &.atencion
    &Color
      color: var(--atencionColor)
    &Fondo::before
      background-color: var(--atencionFondo)
  &.peligro
    &Color
      color: var(--peligroColor)
    &Fondo::before
      background-color: var(--peligroFondo)

  &.alAire
    background-color: transparent
    &:not(:hover) 
      &, &::before
        background-color: transparent

  .elBorde
    +comun.maximizar
    +comun.bradi
    border: 1px solid #00000000
  &.bordeado,
  &.bordeadoSuave
    .elBorde
      z-index: 2
      border-color: currentColor
  &.bordeado,
  &.bordeadoSuave
    &.bordeIncoloro
      &::before
        filter: saturate(0)

  &.casiAlAire
    background-color: transparent
    &::before
      opacity: .25
    &.oscuro::before
      opacity: .5
    &.casi
      &::after
        opacity: .125
      &.oscuro::before
        opacity: .25
  &.bordeado,
  &.bordeadoSuave
    // &::before,
    .elBorde
      opacity: .4
    &.oscuro
      // &::before,
      .elBorde
        opacity: .3
    &.semiBordeado
      // &::before,
      .elBorde
        opacity: .069
      // &.oscuro::before
      // 	opacity: .05
  &.bordeadoSuave
    // &::before,
    .elBorde
      opacity: .1
    &.oscuro
      // &::before,
      .elBorde
        opacity: .3

  &.cargando
    opacity: .8
    cursor: wait
  &.gradiente
    &Color
      color: var(--gradienteColor)
    &Fondo::before
      background: var(--gradienteFondo)
    &Fondo.cargando
      opacity: 1
      &::before
        background: var(--gradienteFondoContinuo)
        background-size: 500% 100%
        background-repeat: repeat
        animation: moverFondo 25s linear infinite
        animation-iteration-count: infinite
        
  &[disabled]:not([disabled="false"]),
  &.desactivado,
  &:disabled
    filter: saturate(.5) opacity(50%)
    cursor: not-allowed

// Transiciones de cambio de estado
.Ui2Boton
  transform: translateY(0px)
  transition: all .3s ease
  &:focus-visible
    outline: .3rem solid currentColor
  &:not(:disabled):not(.cargando):not(.inmovil)
    $distanciaArriba: .2rem
    +comun.hoverDeMouse
      &
        margin-top: $distanciaArriba
        margin-bottom: $distanciaArriba
        transition-duration: .12s

      // Hover de mouse
      &:not(.intocable)
        &:hover
          transition-property: all
          transform: translateY(-#{$distanciaArriba})
          &::after
            transform: translateY(#{$distanciaArriba})
          &::before
            filter: brightness(.975)
          &.alAire
            filter: brightness(.975)
        // Siendo cliqueado
        &:active:hover
          transform: translateY(0)
          &::after
            filter: brightness(1) saturate(1.05)
          .contenidoBoton
            filter: brightness(.95)
        // * Oscuro
        &.oscuro:hover
          &::after
            filter: brightness(.995)
          .contenidoBoton
            filter: brightness(1.1)
          &:active // Clicando
            &::after
              filter: brightness(.97) saturate(1.1)
            .contenidoBoton
              filter: brightness(1.1) saturate(1.1)

</style>
